/** OTO-23: pure hand/color logic for waterfall notes. No PIXI/Tone imports,
 *  no side effects -- kept separate from WaterfallDisplay.tsx (which pulls
 *  in PIXI + the playback store chain) so it stays directly unit-testable. */
import type { Note, PracticeHand } from '@/types';
import { getNoteHand } from './hand';

// OnlinePianist-style colors. Left hand (staff 2) = green, right hand
// (staff 1) = blue.
export const RIGHT_HAND_COLOR = 0x3b82f6;
export const LEFT_HAND_COLOR = 0x22c55e;
export const RIGHT_HAND_ACTIVE = 0x60a5fa; // Lighter blue when sounding
export const LEFT_HAND_ACTIVE = 0x4ade80; // Lighter green when sounding
export const CORRECT_NOTE_COLOR = 0xfbbf24; // Gold — player hit the right note

/** Notes visible in the waterfall for the current hand-practice mode, by
 * staff (`getNoteHand`) -- 'both' shows every note unfiltered. */
export function filterNotesByHand(notes: Note[], hand: PracticeHand): Note[] {
  if (hand === 'both') return notes;
  return notes.filter((n) => getNoteHand(n) === hand);
}

/**
 * Color + opacity for a single falling note bar, by staff (never pitch).
 *
 * Unison design note: unlike `PianoKeyboard`'s `keyColorState` (a single
 * physical key can only glow one color, so ties resolve to staff 1), this
 * function is intentionally NOT tie-broken. Each `Note` here is a real,
 * distinct score event -- if both hands play the same pitch at once, both
 * notes render as their own independently-colored bars. Collapsing that to
 * one color would visually hide a note the user is actually meant to play,
 * which is a worse outcome for a piano-learning app than two overlapping
 * bars. Do not "fix" this to match the keyboard's tie-break rule.
 */
export function noteColorState(
  note: Note,
  currentTime: number,
  activeNotes: Set<number>
): { color: number; alpha: number } {
  const timeUntilNote = note.startTime - currentTime;
  const isRightHand = getNoteHand(note) === 'right';
  const isSounding = timeUntilNote <= 0 && timeUntilNote + note.duration > 0;

  let color: number;
  if (isSounding && activeNotes.has(note.pitch)) {
    color = CORRECT_NOTE_COLOR;
  } else if (isSounding) {
    color = isRightHand ? RIGHT_HAND_ACTIVE : LEFT_HAND_ACTIVE;
  } else {
    color = isRightHand ? RIGHT_HAND_COLOR : LEFT_HAND_COLOR;
  }

  return { color, alpha: isSounding ? 1.0 : 0.85 };
}
